import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import {
  LinkDialogMode,
  LinkDialogTab,
  ManagedUser,
  StagedModel,
  StagedParent,
} from "src/app/models/screens/userManagement.model";
import {
  addAccessibleUser,
  createGuestUser,
  createModelAndLink,
  createParentAndLink,
  deactivateManagedUser,
  getAccessibleUsersOptions,
  getLinkableModels,
  getLinkableParents,
  getManagedUserDetails,
  getRelatedUsers,
  linkExistingUsers,
  removeAccessibleUser,
  saveBatchUsers,
  searchModels,
  setManagedUserCloudStorageLimit,
  suspendManagedUser,
  updateManagedUserProfile,
} from "src/app/utils/backendEndpointCalls.util";

interface UserEditForm {
  email: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
  address: string;
  cloudStorageLimitGb: string;
  suspendDays: string;
}

const UserManagementScreenViewModel = () => {
  const [isSnackbarError, setIsSnackbarError] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [userRole, setUserRole] = useState<string | null>(null);
  const onTabChanged = (_: React.SyntheticEvent, v: number) =>
    setSelectedTab(v);
  const showError = (msg: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(msg);
    setIsDisplayingSnackbar(true);
  };
  const showSuccess = (msg: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(msg);
    setIsDisplayingSnackbar(true);
  };
  const generatePassword = (): string => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$";
    return Array.from(
      { length: 12 },
      () => chars[Math.floor(Math.random() * chars.length)],
    ).join("");
  };
  const makeTempId = () => `tmp_${Date.now()}_${Math.random()}`;
  const [userSearchText, setUserSearchText] = useState("");
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [allUsers, setAllUsers] = useState<ManagedUser[]>([]);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [relatedUsersMap, setRelatedUsersMap] = useState<
    Record<string, ManagedUser[]>
  >({});
  const [loadingRelatedId, setLoadingRelatedId] = useState<string | null>(null);
  const [loadingUserDetailsId, setLoadingUserDetailsId] = useState<
    string | null
  >(null);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [applyingStorageLimitUserId, setApplyingStorageLimitUserId] = useState<
    string | null
  >(null);
  const [deactivatingUserId, setDeactivatingUserId] = useState<string | null>(
    null,
  );
  const [suspendingUserId, setSuspendingUserId] = useState<string | null>(null);
  const [removingAccessibleUserKey, setRemovingAccessibleUserKey] = useState<
    string | null
  >(null);
  const [userEditForms, setUserEditForms] = useState<
    Record<string, UserEditForm>
  >({});
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadUsers = useCallback(
    async (query: string) => {
      if (userRole !== "Admin") return;
      setIsSearchingUsers(true);
      const response = await searchModels(query);
      setIsSearchingUsers(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      setAllUsers(response.users ?? []);
    },
    [userRole],
  );

  useEffect(() => {
    const userRoleFromStorage = localStorage.getItem(
      localStorageConstants.ROLE,
    );
    if (userRoleFromStorage) setUserRole(userRoleFromStorage);
  }, []);

  useEffect(() => {
    loadUsers("");
  }, [loadUsers, userRole]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(
      () => loadUsers(userSearchText.trim()),
      300,
    );
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [loadUsers, userSearchText]);

  const refreshRelated = async (userId: string) => {
    const response = await getRelatedUsers(userId);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setRelatedUsersMap((prev) => ({
      ...prev,
      [userId]: response.related ?? [],
    }));
  };

  const loadUserDetails = async (userId: string) => {
    setLoadingUserDetailsId(userId);
    const response = await getManagedUserDetails(userId);
    setLoadingUserDetailsId(null);

    if (!response.success) {
      showError(response.error);
      return;
    }

    const user = response.user;
    setUserEditForms((prev) => ({
      ...prev,
      [userId]: {
        email: user.email ?? "",
        phoneNumber: user.phoneNumber ?? "",
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        gender: user.gender ?? "",
        dateOfBirth: user.dateOfBirth ?? "",
        address: user.address ?? "",
        cloudStorageLimitGb:
          user.cloudStorageLimitGb !== undefined &&
          user.cloudStorageLimitGb !== null
            ? String(user.cloudStorageLimitGb)
            : "",
        suspendDays: "",
      },
    }));
  };

  const onUserRowClicked = async (user: ManagedUser) => {
    if (expandedUserId === user.id) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(user.id);
    if (!relatedUsersMap[user.id]) {
      setLoadingRelatedId(user.id);
      await refreshRelated(user.id);
      setLoadingRelatedId(null);
    }
    if (!userEditForms[user.id]) {
      await loadUserDetails(user.id);
    }
  };

  const updateUserEditField = (
    userId: string,
    field: keyof UserEditForm,
    value: string,
  ) => {
    setUserEditForms((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value,
      },
    }));
  };

  const onSaveUserEdits = async (user: ManagedUser) => {
    const form = userEditForms[user.id];
    if (!form) return;

    if (!form.email.trim()) {
      showError("Email is required.");
      return;
    }
    if (!form.firstName.trim()) {
      showError("First name is required.");
      return;
    }
    if (!form.lastName.trim()) {
      showError("Last name is required.");
      return;
    }
    if (
      (user.role === "Parent" || user.role === "Guest") &&
      !form.phoneNumber.trim()
    ) {
      showError("Phone number is required for parent and guest users.");
      return;
    }
    if (user.role === "Model") {
      if (!form.gender.trim()) {
        showError("Gender is required for model users.");
        return;
      }
      if (!form.dateOfBirth.trim()) {
        showError("Date of birth is required for model users.");
        return;
      }
    }

    if (user.role === "Parent" && !form.address.trim()) {
      showError("Address is required for parent users.");
      return;
    }

    setSavingUserId(user.id);

    const response = await updateManagedUserProfile(user.id, {
      email: form.email.trim(),
      phoneNumber: form.phoneNumber.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      gender: user.role === "Model" ? form.gender.trim() : undefined,
      dateOfBirth: user.role === "Model" ? form.dateOfBirth.trim() : undefined,
      address: user.role === "Parent" ? form.address.trim() : undefined,
    });

    setSavingUserId(null);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setAllUsers((prev) =>
      prev.map((u) =>
        u.id === user.id
          ? {
              ...u,
              email: form.email.trim(),
              phoneNumber: form.phoneNumber.trim(),
              firstName: form.firstName.trim(),
              lastName: form.lastName.trim(),
            }
          : u,
      ),
    );

    await loadUserDetails(user.id);
    showSuccess("User details updated successfully.");
  };

  const onApplyStorageLimit = async (user: ManagedUser) => {
    const form = userEditForms[user.id];
    if (!form) return;

    if (form.cloudStorageLimitGb.trim() === "") {
      showError("Please provide a cloud storage limit in GB.");
      return;
    }

    const parsedLimit = Number(form.cloudStorageLimitGb);
    if (Number.isNaN(parsedLimit) || parsedLimit < 0) {
      showError("Cloud storage limit must be a valid non-negative number.");
      return;
    }

    setApplyingStorageLimitUserId(user.id);
    const response = await setManagedUserCloudStorageLimit(
      user.id,
      parsedLimit,
    );
    setApplyingStorageLimitUserId(null);

    if (!response.success) {
      showError(response.error);
      return;
    }

    await loadUserDetails(user.id);
    showSuccess("Cloud storage limit updated successfully.");
  };

  const onDeactivateUser = async (user: ManagedUser) => {
    setDeactivatingUserId(user.id);
    const response = await deactivateManagedUser(user.id);
    setDeactivatingUserId(null);

    if (!response.success) {
      showError(response.error);
      return;
    }

    showSuccess("Account deactivated successfully.");
    await loadUsers(userSearchText.trim());
  };

  const onSuspendUser = async (user: ManagedUser) => {
    const form = userEditForms[user.id];
    if (!form) return;

    if (!form.suspendDays.trim()) {
      showError(
        "Please provide how many days the account should be suspended.",
      );
      return;
    }

    const parsedDays = Number(form.suspendDays);
    if (Number.isNaN(parsedDays) || parsedDays <= 0) {
      showError("Suspend days must be a valid positive number.");
      return;
    }

    setSuspendingUserId(user.id);
    const response = await suspendManagedUser(user.id, parsedDays);
    setSuspendingUserId(null);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setUserEditForms((prev) => ({
      ...prev,
      [user.id]: {
        ...prev[user.id],
        suspendDays: "",
      },
    }));

    showSuccess("Account suspended successfully.");
  };

  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkDialogMode, setLinkDialogMode] =
    useState<LinkDialogMode>("addParentToModel");
  const [linkDialogAnchorUser, setLinkDialogAnchorUser] =
    useState<ManagedUser | null>(null);
  const [linkDialogTab, setLinkDialogTab] = useState<LinkDialogTab>("existing");
  const onLinkDialogTabChanged = (
    _: React.SyntheticEvent,
    v: LinkDialogTab,
  ) => {
    setLinkDialogTab(v);
  };

  const [pickerSearchText, setPickerSearchText] = useState("");
  const [isLoadingPickerUsers, setIsLoadingPickerUsers] = useState(false);
  const [pickerUsers, setPickerUsers] = useState<ManagedUser[]>([]);
  const [isLinkingExisting, setIsLinkingExisting] = useState(false);
  const pickerSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadPickerUsers = useCallback(
    async (query: string, mode: LinkDialogMode) => {
      if (!linkDialogAnchorUser) return;
      setIsLoadingPickerUsers(true);
      if (mode === "addParentToModel") {
        const response = await getLinkableParents(
          query,
          linkDialogAnchorUser.id,
        );
        setIsLoadingPickerUsers(false);
        if (!response.success) {
          showError(response.error);
          return;
        }
        setPickerUsers(response.parents);
      } else {
        const response = await getLinkableModels(
          query,
          linkDialogAnchorUser.id,
        );
        setIsLoadingPickerUsers(false);
        if (!response.success) {
          showError(response.error);
          return;
        }
        setPickerUsers(response.models);
      }
    },
    [linkDialogAnchorUser],
  );

  useEffect(() => {
    if (!isLinkDialogOpen) return;
    if (pickerSearchRef.current) clearTimeout(pickerSearchRef.current);
    pickerSearchRef.current = setTimeout(() => {
      loadPickerUsers(pickerSearchText.trim(), linkDialogMode);
    }, 300);
    return () => {
      if (pickerSearchRef.current) clearTimeout(pickerSearchRef.current);
    };
  }, [pickerSearchText, isLinkDialogOpen, linkDialogMode, loadPickerUsers]);

  const openLinkDialog = (anchor: ManagedUser, mode: LinkDialogMode) => {
    setLinkDialogAnchorUser(anchor);
    setLinkDialogMode(mode);
    setLinkDialogTab("existing");
    setPickerSearchText("");
    setPickerUsers([]);
    resetNewAccountForm();
    setIsLinkDialogOpen(true);
    loadPickerUsers("", mode);
  };

  const closeLinkDialog = () => {
    setIsLinkDialogOpen(false);
    setLinkDialogAnchorUser(null);
    resetNewAccountForm();
  };

  const onLinkExistingUser = async (pickedUser: ManagedUser) => {
    if (!linkDialogAnchorUser) return;
    const modelId =
      linkDialogMode === "addParentToModel"
        ? linkDialogAnchorUser.id
        : pickedUser.id;
    const parentId =
      linkDialogMode === "addParentToModel"
        ? pickedUser.id
        : linkDialogAnchorUser.id;
    setIsLinkingExisting(true);
    const response = await linkExistingUsers(modelId, parentId);
    setIsLinkingExisting(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    await refreshRelated(linkDialogAnchorUser.id);
    showSuccess("Accounts linked successfully.");
    closeLinkDialog();
  };

  const [isAccessibleUsersDialogOpen, setIsAccessibleUsersDialogOpen] =
    useState(false);
  const [accessibleUsersAnchorGuest, setAccessibleUsersAnchorGuest] =
    useState<ManagedUser | null>(null);
  const [accessibleUsersSearchText, setAccessibleUsersSearchText] =
    useState("");
  const [accessibleUsersOptions, setAccessibleUsersOptions] = useState<
    ManagedUser[]
  >([]);
  const [isLoadingAccessibleUsersOptions, setIsLoadingAccessibleUsersOptions] =
    useState(false);
  const [addingAccessibleUserId, setAddingAccessibleUserId] = useState<
    string | null
  >(null);
  const accessibleUsersSearchRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const loadAccessibleUsersOptions = useCallback(
    async (guestUserId: string, query: string) => {
      setIsLoadingAccessibleUsersOptions(true);
      const response = await getAccessibleUsersOptions(guestUserId, query);
      setIsLoadingAccessibleUsersOptions(false);

      if (!response.success) {
        showError(response.error);
        return;
      }

      setAccessibleUsersOptions(response.users ?? []);
    },
    [],
  );

  useEffect(() => {
    if (!isAccessibleUsersDialogOpen || !accessibleUsersAnchorGuest) return;

    if (accessibleUsersSearchRef.current)
      clearTimeout(accessibleUsersSearchRef.current);

    accessibleUsersSearchRef.current = setTimeout(() => {
      loadAccessibleUsersOptions(
        accessibleUsersAnchorGuest.id,
        accessibleUsersSearchText.trim(),
      );
    }, 300);

    return () => {
      if (accessibleUsersSearchRef.current)
        clearTimeout(accessibleUsersSearchRef.current);
    };
  }, [
    isAccessibleUsersDialogOpen,
    accessibleUsersAnchorGuest,
    accessibleUsersSearchText,
    loadAccessibleUsersOptions,
  ]);

  const openAccessibleUsersDialog = (guestUser: ManagedUser) => {
    setAccessibleUsersAnchorGuest(guestUser);
    setAccessibleUsersSearchText("");
    setAccessibleUsersOptions([]);
    setIsAccessibleUsersDialogOpen(true);
    loadAccessibleUsersOptions(guestUser.id, "");
  };

  const closeAccessibleUsersDialog = () => {
    setIsAccessibleUsersDialogOpen(false);
    setAccessibleUsersAnchorGuest(null);
    setAccessibleUsersSearchText("");
    setAccessibleUsersOptions([]);
  };

  const onAddAccessibleUser = async (pickedUser: ManagedUser) => {
    if (!accessibleUsersAnchorGuest) return;

    setAddingAccessibleUserId(pickedUser.id);
    const response = await addAccessibleUser(
      accessibleUsersAnchorGuest.id,
      pickedUser.id,
    );
    setAddingAccessibleUserId(null);

    if (!response.success) {
      showError(response.error);
      return;
    }

    await refreshRelated(accessibleUsersAnchorGuest.id);
    await loadAccessibleUsersOptions(
      accessibleUsersAnchorGuest.id,
      accessibleUsersSearchText.trim(),
    );
    showSuccess("Accessible user added successfully.");
  };

  const onRemoveAccessibleUser = async (
    guestUserId: string,
    accessibleUserId: string,
  ) => {
    const removalKey = `${guestUserId}-${accessibleUserId}`;
    setRemovingAccessibleUserKey(removalKey);

    const response = await removeAccessibleUser(guestUserId, accessibleUserId);

    setRemovingAccessibleUserKey(null);

    if (!response.success) {
      showError(response.error);
      return;
    }

    await refreshRelated(guestUserId);

    if (accessibleUsersAnchorGuest?.id === guestUserId) {
      await loadAccessibleUsersOptions(
        guestUserId,
        accessibleUsersSearchText.trim(),
      );
    }

    showSuccess("Accessible user removed successfully.");
  };

  const [newAccountPassword, setNewAccountPassword] = useState("");
  const [newAccountEmail, setNewAccountEmail] = useState("");
  const [newAccountPhoneNumber, setNewAccountPhoneNumber] = useState("");
  const [newModelFirstName, setNewModelFirstName] = useState("");
  const [newModelLastName, setNewModelLastName] = useState("");
  const [newModelDob, setNewModelDob] = useState<Dayjs | null>(null);
  const [newModelGender, setNewModelGender] = useState("");
  const [newParentName, setNewParentName] = useState("");
  const [newParentLastName, setNewParentLastName] = useState("");
  const [newParentAddress, setNewParentAddress] = useState("");
  const [isCreatingNewAndLinking, setIsCreatingNewAndLinking] = useState(false);

  const resetNewAccountForm = () => {
    setNewAccountPassword("");
    setNewAccountEmail("");
    setNewAccountPhoneNumber("");
    setNewModelFirstName("");
    setNewModelLastName("");
    setNewModelDob(null);
    setNewModelGender("");
    setNewParentName("");
    setNewParentLastName("");
    setNewParentAddress("");
  };

  const onCreateNewAndLink = async () => {
    if (!linkDialogAnchorUser) return;
    if (!newAccountEmail.trim()) {
      showError("Email is required.");
      return;
    }
    if (!newAccountPassword.trim()) {
      showError("Password is required.");
      return;
    }
    setIsCreatingNewAndLinking(true);
    if (linkDialogMode === "addParentToModel") {
      if (!newParentName.trim()) {
        showError("First name is required.");
        setIsCreatingNewAndLinking(false);
        return;
      }
      if (!newParentLastName.trim()) {
        showError("Last name is required.");
        setIsCreatingNewAndLinking(false);
        return;
      }
      if (!newAccountPhoneNumber.trim()) {
        showError("Phone number is required.");
        setIsCreatingNewAndLinking(false);
        return;
      }
      if (!newParentAddress.trim()) {
        showError("Address is required.");
        setIsCreatingNewAndLinking(false);
        return;
      }
      const response = await createParentAndLink(
        newParentName.trim(),
        newParentLastName.trim(),
        newAccountEmail.trim(),
        newAccountPhoneNumber.trim(),
        newParentAddress.trim(),
        newAccountPassword,
        linkDialogAnchorUser.id,
      );
      setIsCreatingNewAndLinking(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
    } else {
      if (!newModelFirstName.trim()) {
        showError("First name is required.");
        setIsCreatingNewAndLinking(false);
        return;
      }
      if (!newModelLastName.trim()) {
        showError("Last name is required.");
        setIsCreatingNewAndLinking(false);
        return;
      }
      if (!newModelDob) {
        showError("Date of birth is required.");
        setIsCreatingNewAndLinking(false);
        return;
      }
      if (!newModelGender.trim()) {
        showError("Gender is required.");
        setIsCreatingNewAndLinking(false);
        return;
      }
      const response = await createModelAndLink(
        newModelFirstName.trim(),
        newModelLastName.trim(),
        newAccountEmail.trim(),
        newAccountPhoneNumber.trim(),
        newAccountPassword,
        newModelDob.format("YYYY-MM-DD"),
        newModelGender.trim(),
        linkDialogAnchorUser.id,
      );
      setIsCreatingNewAndLinking(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
    }

    await refreshRelated(linkDialogAnchorUser.id);
    await loadUsers(userSearchText.trim());
    showSuccess("Account created and linked successfully.");
    closeLinkDialog();
  };

  const [stagedModels, setStagedModels] = useState<StagedModel[]>([]);
  const [stagedParents, setStagedParents] = useState<StagedParent[]>([]);
  const [isSavingBatch, setIsSavingBatch] = useState(false);
  const [isModelDialogOpen, setIsModelDialogOpen] = useState(false);
  const [modelFirstName, setModelFirstName] = useState("");
  const [modelLastName, setModelLastName] = useState("");
  const [modelEmail, setModelEmail] = useState("");
  const [modelPhoneNumber, setModelPhoneNumber] = useState("");
  const [modelPassword, setModelPassword] = useState("");
  const [modelDob, setModelDob] = useState<Dayjs | null>(null);
  const [modelGender, setModelGender] = useState("");
  const [modelLinkedParentTempIds, setModelLinkedParentTempIds] = useState<
    string[]
  >([]);

  const openModelDialog = () => {
    setModelFirstName("");
    setModelLastName("");
    setModelEmail("");
    setModelPhoneNumber("");
    setModelPassword("");
    setModelDob(null);
    setModelGender("");
    setModelLinkedParentTempIds([]);
    setIsModelDialogOpen(true);
  };
  const closeModelDialog = () => setIsModelDialogOpen(false);

  const isModelMinor = (): boolean => {
    if (!modelDob) return false;
    return dayjs().diff(modelDob, "year") < 18;
  };

  const onConfirmAddModel = () => {
    if (!modelFirstName.trim()) {
      showError("First name is required.");
      return;
    }
    if (!modelLastName.trim()) {
      showError("Last name is required.");
      return;
    }
    if (!modelEmail.trim()) {
      showError("Email is required.");
      return;
    }
    if (!modelPassword.trim()) {
      showError("Password is required.");
      return;
    }
    if (!modelDob) {
      showError("Date of birth is required.");
      return;
    }
    if (!modelGender.trim()) {
      showError("Gender is required.");
      return;
    }
    const newModel: StagedModel = {
      _tempId: makeTempId(),
      firstName: modelFirstName.trim(),
      lastName: modelLastName.trim(),
      email: modelEmail.trim(),
      phoneNumber: modelPhoneNumber.trim(),
      password: modelPassword,
      dateOfBirth: modelDob.format("YYYY-MM-DD"),
      gender: modelGender.trim(),
      linkedParentTempIds: modelLinkedParentTempIds,
    };
    setStagedModels((prev) => [...prev, newModel]);
    closeModelDialog();
  };

  const removeStagedModel = (id: string) =>
    setStagedModels((prev) => prev.filter((m) => m._tempId !== id));

  const toggleParentLink = (parentTempId: string) => {
    setModelLinkedParentTempIds((prev) =>
      prev.includes(parentTempId)
        ? prev.filter((id) => id !== parentTempId)
        : [...prev, parentTempId],
    );
  };

  const [isParentDialogOpen, setIsParentDialogOpen] = useState(false);
  const [parentFirstName, setParentFirstName] = useState("");
  const [parentLastName, setParentLastName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [parentPhoneNumber, setParentPhoneNumber] = useState("");
  const [parentAddress, setParentAddress] = useState("");
  const [parentPassword, setParentPassword] = useState("");

  const openParentDialog = () => {
    setParentFirstName("");
    setParentLastName("");
    setParentEmail("");
    setParentPhoneNumber("");
    setParentAddress("");
    setParentPassword("");
    setIsParentDialogOpen(true);
  };
  const closeParentDialog = () => setIsParentDialogOpen(false);

  const onConfirmAddParent = () => {
    if (!parentFirstName.trim()) {
      showError("First name is required.");
      return;
    }
    if (!parentLastName.trim()) {
      showError("Last name is required.");
      return;
    }
    if (!parentEmail.trim()) {
      showError("Email is required.");
      return;
    }
    if (!parentPhoneNumber.trim()) {
      showError("Phone number is required.");
      return;
    }
    if (!parentAddress.trim()) {
      showError("Address is required.");
      return;
    }
    if (!parentPassword.trim()) {
      showError("Password is required.");
      return;
    }
    const newParent: StagedParent = {
      _tempId: makeTempId(),
      firstName: parentFirstName.trim(),
      lastName: parentLastName.trim(),
      email: parentEmail.trim(),
      phoneNumber: parentPhoneNumber.trim(),
      address: parentAddress.trim(),
      password: parentPassword,
    };
    setStagedParents((prev) => [...prev, newParent]);
    closeParentDialog();
  };

  const removeStagedParent = (id: string) => {
    setStagedParents((prev) => prev.filter((p) => p._tempId !== id));
    setStagedModels((prev) =>
      prev.map((m) => ({
        ...m,
        linkedParentTempIds: m.linkedParentTempIds.filter((pid) => pid !== id),
      })),
    );
  };

  const hasMinorWithoutParent = (): boolean =>
    stagedModels.some((m) => {
      const age = dayjs().diff(dayjs(m.dateOfBirth), "year");
      return age < 18 && stagedParents.length === 0;
    });

  const onSaveBatch = async () => {
    if (stagedModels.length === 0 && stagedParents.length === 0) {
      showError("Nothing to save. Add at least one user.");
      return;
    }
    if (hasMinorWithoutParent()) {
      showError(
        "One or more minor models have no linked parent. Please link a parent before saving.",
      );
      return;
    }
    setIsSavingBatch(true);
    const response = await saveBatchUsers(stagedModels, stagedParents);
    setIsSavingBatch(false);

    if (!response.success) {
      showError(response.error);
      return;
    }
    setStagedModels([]);
    setStagedParents([]);
    showSuccess("Users saved successfully.");
    loadUsers("");
  };

  const [guestFirstName, setGuestFirstName] = useState("");
  const [guestLastName, setGuestLastName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhoneNumber, setGuestPhoneNumber] = useState("");
  const [guestPassword, setGuestPassword] = useState("");
  const [isCreatingGuest, setIsCreatingGuest] = useState(false);

  const onCreateGuest = async () => {
    if (!guestFirstName.trim()) {
      showError("First name is required.");
      return;
    }
    if (!guestLastName.trim()) {
      showError("Last name is required.");
      return;
    }
    if (!guestEmail.trim()) {
      showError("Email is required.");
      return;
    }
    if (!guestPhoneNumber.trim()) {
      showError("Phone number is required.");
      return;
    }
    if (!guestPassword.trim()) {
      showError("Password is required.");
      return;
    }
    setIsCreatingGuest(true);
    const response = await createGuestUser(
      guestFirstName.trim(),
      guestLastName.trim(),
      guestEmail.trim(),
      guestPhoneNumber.trim(),
      guestPassword,
    );
    setIsCreatingGuest(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setGuestFirstName("");
    setGuestLastName("");
    setGuestEmail("");
    setGuestPhoneNumber("");
    setGuestPassword("");
    showSuccess("Guest user created successfully.");
    loadUsers("");
  };

  return {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    selectedTab,
    onTabChanged,
    userSearchText,
    setUserSearchText,
    isSearchingUsers,
    allUsers,
    expandedUserId,
    relatedUsersMap,
    loadingRelatedId,
    loadingUserDetailsId,
    savingUserId,
    applyingStorageLimitUserId,
    deactivatingUserId,
    suspendingUserId,
    removingAccessibleUserKey,
    userEditForms,
    onUserRowClicked,
    updateUserEditField,
    onSaveUserEdits,
    onApplyStorageLimit,
    onDeactivateUser,
    onSuspendUser,
    isLinkDialogOpen,
    linkDialogMode,
    linkDialogTab,
    onLinkDialogTabChanged,
    linkDialogAnchorUser,
    openLinkDialog,
    closeLinkDialog,
    pickerSearchText,
    setPickerSearchText,
    isLoadingPickerUsers,
    pickerUsers,
    isLinkingExisting,
    onLinkExistingUser,
    isAccessibleUsersDialogOpen,
    accessibleUsersAnchorGuest,
    accessibleUsersSearchText,
    setAccessibleUsersSearchText,
    accessibleUsersOptions,
    isLoadingAccessibleUsersOptions,
    addingAccessibleUserId,
    openAccessibleUsersDialog,
    closeAccessibleUsersDialog,
    onAddAccessibleUser,
    onRemoveAccessibleUser,
    newAccountPassword,
    setNewAccountPassword,
    newAccountEmail,
    setNewAccountEmail,
    newAccountPhoneNumber,
    setNewAccountPhoneNumber,
    newModelFirstName,
    setNewModelFirstName,
    newModelLastName,
    setNewModelLastName,
    newModelDob,
    setNewModelDob,
    newModelGender,
    setNewModelGender,
    newParentName,
    setNewParentName,
    newParentLastName,
    setNewParentLastName,
    newParentAddress,
    setNewParentAddress,
    isCreatingNewAndLinking,
    onCreateNewAndLink,
    stagedModels,
    stagedParents,
    isSavingBatch,
    onSaveBatch,
    removeStagedModel,
    removeStagedParent,
    isModelDialogOpen,
    openModelDialog,
    closeModelDialog,
    modelFirstName,
    setModelFirstName,
    modelLastName,
    setModelLastName,
    modelEmail,
    setModelEmail,
    modelPhoneNumber,
    setModelPhoneNumber,
    modelPassword,
    setModelPassword,
    modelDob,
    setModelDob,
    modelGender,
    setModelGender,
    modelLinkedParentTempIds,
    toggleParentLink,
    onConfirmAddModel,
    isModelMinor,
    isParentDialogOpen,
    openParentDialog,
    closeParentDialog,
    parentFirstName,
    setParentFirstName,
    parentLastName,
    setParentLastName,
    parentEmail,
    setParentEmail,
    parentPhoneNumber,
    setParentPhoneNumber,
    parentAddress,
    setParentAddress,
    parentPassword,
    setParentPassword,
    onConfirmAddParent,
    guestFirstName,
    setGuestFirstName,
    guestLastName,
    setGuestLastName,
    guestEmail,
    setGuestEmail,
    guestPhoneNumber,
    setGuestPhoneNumber,
    guestPassword,
    setGuestPassword,
    isCreatingGuest,
    onCreateGuest,
    generatePassword,
    userRole,
  };
};

export default UserManagementScreenViewModel;
